import assert from 'assert-ts';
import get from 'lodash/get';
import set from 'lodash/set';
import type {Thesaurus, ThesaurusNode} from '../types';
import {findNodePath} from '../functions/findNodePath';

export const modifyNeoThesaurus = (thesaurus: Thesaurus): Thesaurus => {
  if (thesaurus.id !== 'neo') {
    return thesaurus;
  }

  return splitFictionalPeople(thesaurus);
};

/**
 * Splits the "Fiktive personer" (Fictional People) node into sub-nodes based on
 * the first letter of each child's label. We do this because there are too many
 * nodes to render at once.
 */
export const splitFictionalPeople = (thesaurus: Thesaurus): Thesaurus => {
  if (!thesaurus.children) {
    return thesaurus;
  }

  const fictionalPeopleNodePath = findNodePath(
    'https://id.nb.no/vocabulary/neo/11945',
    thesaurus,
  );

  if (!fictionalPeopleNodePath) {
    assert.soft(
      false,
      'splitFictionalPeople: fictional people node not found in neo thesaurus',
    );
    return thesaurus;
  }

  const fictionalPeopleNode: ThesaurusNode = get(
    thesaurus,
    fictionalPeopleNodePath,
  );
  set(
    thesaurus,
    fictionalPeopleNodePath.join('.'),
    splitChildrenOfThesaurusNodeByFirstLetter(fictionalPeopleNode),
  );

  return thesaurus;
};

export const splitChildrenOfThesaurusNodeByFirstLetter = (
  node: ThesaurusNode,
): ThesaurusNode => {
  const letterMap: Record<string, ThesaurusNode> = {};

  (node.children ?? []).forEach(child => {
    const firstLetter = child.label[0] ? child.label[0].toUpperCase() : 'A';
    if (!letterMap[firstLetter]) {
      letterMap[firstLetter] = {
        id: `letter-${firstLetter}`,
        code: `letter-${firstLetter}`,
        label: firstLetter,
        selectable: false,
        deactivated: false,
        children: [],
      };
    }

    letterMap[firstLetter].children!.push(child);
  });

  return {
    ...node,
    children: Object.values(letterMap),
  };
};
